import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useCallback, useMemo } from 'react';
import { MultiColumn, MultiColumnGroup, MultiColumnItem } from 'foundations-components/transitional/components/MultiColumn';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { Flex } from 'foundations-components/components/Flex';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import { useFetchUsersWithInstallAccess } from '../../../hooks/queries/useFetchUsersWithInstallAccess';
import { AppInstallPermissionsTag, SuperAdminTag } from '../../UsersWithInstallAccessDrawer/UsersWithInstallAccessTable';
import { useInstallPermissions } from '../../../contexts/InstallPermissionsContext';
import { UserName } from '../../UserName';
import { TeamAccessTag } from '../TeamAccessTag/TeamAccessTag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function UserAccessControl({
  isSuperAdmin,
  isIntegrationsManagementWriteUser,
  userId,
  initiallySelected
}) {
  var _updatedUsers$userId;
  const {
    updatedUsers,
    setUpdatedUsers
  } = useInstallPermissions();
  const handleToggleChange = useCallback(e => {
    const checked = e.target.checked;
    setUpdatedUsers(prev => {
      if (checked === initiallySelected) {
        const rest = _objectWithoutPropertiesLoose(prev, [userId].map(_toPropertyKey));
        return rest;
      }
      return Object.assign({}, prev, {
        [userId]: checked
      });
    });
  }, [setUpdatedUsers, userId, initiallySelected]);
  if (isSuperAdmin) {
    return /*#__PURE__*/_jsx(SuperAdminTag, {});
  }
  if (isIntegrationsManagementWriteUser) {
    return /*#__PURE__*/_jsx(AppInstallPermissionsTag, {});
  }
  const isChecked = (_updatedUsers$userId = updatedUsers[userId]) !== null && _updatedUsers$userId !== void 0 ? _updatedUsers$userId : initiallySelected;
  return /*#__PURE__*/_jsx(Toggle, {
    "data-selenium": "install-permissions-user-toggle",
    size: "extra-small",
    checked: isChecked,
    onChange: handleToggleChange
  });
}
function UserColumnGroup({
  isSuperAdmin,
  isIntegrationsManagementWriteUser,
  firstName,
  lastName,
  email,
  userId,
  initiallySelected,
  teamNames
}) {
  return /*#__PURE__*/_jsxs(MultiColumnGroup, {
    children: [/*#__PURE__*/_jsx(MultiColumnItem, {
      children: /*#__PURE__*/_jsx(UserName, {
        user: {
          id: userId,
          firstName,
          lastName,
          email
        }
      })
    }), /*#__PURE__*/_jsx(MultiColumnItem, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "end",
        gap: "200",
        children: [!isSuperAdmin && !isIntegrationsManagementWriteUser && /*#__PURE__*/_jsx(TeamAccessTag, {
          teamNames: teamNames
        }), /*#__PURE__*/_jsx(UserAccessControl, {
          isSuperAdmin: isSuperAdmin,
          isIntegrationsManagementWriteUser: isIntegrationsManagementWriteUser,
          userId: userId,
          initiallySelected: initiallySelected
        })]
      })
    })]
  });
}
function getUserPriority(userId, superAdmins, integrationsManagementWriteUsers) {
  if (superAdmins[userId]) return 2;
  if (integrationsManagementWriteUsers[userId]) return 1;
  return 0;
}
export function UserList({
  results,
  userTeamAccessMap
}) {
  const {
    superAdmins = {},
    integrationsManagementWriteUsers = {}
  } = useFetchUsersWithInstallAccess();
  const {
    initialUserIds
  } = useInstallPermissions();
  const sortedResultsWithMetadata = useMemo(() => {
    return results.map(result => {
      var _getPropertyValue, _getPropertyValue2, _getPropertyValue3, _userTeamAccessMap$ge;
      const userId = Number(getPropertyValue(result, 'hs_internal_user_id'));
      const email = (_getPropertyValue = getPropertyValue(result, 'hs_email')) !== null && _getPropertyValue !== void 0 ? _getPropertyValue : '';
      const firstName = (_getPropertyValue2 = getPropertyValue(result, 'hs_given_name')) !== null && _getPropertyValue2 !== void 0 ? _getPropertyValue2 : '';
      const lastName = (_getPropertyValue3 = getPropertyValue(result, 'hs_family_name')) !== null && _getPropertyValue3 !== void 0 ? _getPropertyValue3 : '';
      const isSuperAdmin = !!superAdmins[userId];
      const isIntegrationsManagementWriteUser = !!integrationsManagementWriteUsers[userId];
      const initiallySelected = initialUserIds.has(userId);
      return {
        result,
        userId,
        isSuperAdmin,
        firstName,
        lastName,
        email,
        isIntegrationsManagementWriteUser,
        initiallySelected,
        teamNames: (_userTeamAccessMap$ge = userTeamAccessMap.get(userId)) !== null && _userTeamAccessMap$ge !== void 0 ? _userTeamAccessMap$ge : [],
        priority: getUserPriority(userId, superAdmins, integrationsManagementWriteUsers)
      };
    }).sort((a, b) => a.priority - b.priority);
  }, [results, superAdmins, integrationsManagementWriteUsers, initialUserIds, userTeamAccessMap]);
  return /*#__PURE__*/_jsx(MultiColumn, {
    "data-selenium": "install-permissions-user-list",
    children: sortedResultsWithMetadata.map(({
      result,
      userId,
      isSuperAdmin,
      isIntegrationsManagementWriteUser,
      firstName,
      lastName,
      email,
      initiallySelected,
      teamNames
    }) => {
      return /*#__PURE__*/_jsx(UserColumnGroup, {
        userId: userId,
        isSuperAdmin: isSuperAdmin,
        isIntegrationsManagementWriteUser: isIntegrationsManagementWriteUser,
        firstName: firstName,
        lastName: lastName,
        email: email,
        initiallySelected: initiallySelected,
        teamNames: teamNames
      }, result.objectId);
    })
  });
}